"use client";
import React, { useState, useRef, useEffect } from "react";
import Image from "@/components/atoms/image";
import PlayButton from "@/components/atoms/play-icon";
import { useTranslations } from "next-intl";

interface VideoPlayerProps {
  videoUrl?: string;
  videoSource?: "youtube" | "upload" | string;
  posterImage?: string;
}

export default function VideoPlayer({ videoUrl, videoSource, posterImage }: VideoPlayerProps) {
  const t = useTranslations("Carlist");
  const [isPlaying, setIsPlaying] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  
  const getYoutubeVideoId = (url: string) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  const isYoutubeVideo = videoSource === "youtube" || (videoUrl && (videoUrl.includes("youtube") || videoUrl.includes("youtu.be")));
  
  const youtubeVideoId = isYoutubeVideo && videoUrl ? getYoutubeVideoId(videoUrl) : null;
  
  useEffect(() => {
    if (isPlaying && !isYoutubeVideo && videoRef.current) {
      videoRef.current.focus();
    }
  }, [isPlaying, isYoutubeVideo]);

  const handlePlayClick = () => {
    setIsPlaying(true);
  };

  if (!videoUrl) {
    return null;
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handlePlayClick();
    }
    else if (event.key === 'Escape' && !isYoutubeVideo) {
      event.preventDefault();
      setIsPlaying(false);
    }
  };


  if (isPlaying && isYoutubeVideo && youtubeVideoId) {
    return (
      <div className="w-full aspect-video">
        <iframe
          width="100%"
          height="100%"
          src={`https://www.youtube.com/embed/${youtubeVideoId}?autoplay=1`}
          title="YouTube video player"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>
    );
  }
  
  if (isPlaying && !isYoutubeVideo) {
    return (
      <div className="w-full aspect-video">
        <video
          ref={videoRef}
          src={videoUrl}
          controls
          autoPlay
          tabIndex={0}
          className="w-full h-full rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>
    );
  }

  return (
    <div 
      ref={containerRef}
      className="w-full aspect-video relative cursor-pointer group focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
      onClick={handlePlayClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="button"
      aria-label={t("playVideo")}
    >
      {posterImage ? (
        <Image
          src={posterImage}
          alt={t("videoCover")}
          fill
          sizes="100vw"
          className="object-cover rounded"
          priority
        />
      ) : (
        <div className="w-full h-full bg-neutral-200 rounded" />
      )}
      
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="rounded-full z-30 p-3 transform transition-transform duration-200 group-hover:scale-110 group-focus:scale-110">
          <PlayButton size={64} color="#ffffff" hoverColor="#ffffff" />
        </div>
      </div>
      
      <div className="absolute inset-0 z-20 opacity-0 group-hover:opacity-100 group-focus:opacity-100 transition-opacity bg-bupi-hoverGradient pointer-events-none" />
    </div>
  );
}